import { MutableRefObject } from 'react';
import { AgGridReact } from '@ag-grid-community/react';
import {
    CellStyle,
    ProcessCellForExportParams,
    ValueGetterParams,
    ValueSetterParams,
    ITooltipParams,
    GridApi,
    IAggFuncParams,
    CellClassParams,
    IRowNode,
    EditableCallbackParams,
    RangeSelectionChangedEvent,
    RowDragEndEvent,
    SelectionChangedEvent,
    CellRange,
    Column,
    CellContextMenuEvent
} from '@ag-grid-community/core';

import { EXTENDED_COST_TOOLTIP_MSG, EXTENDED_DISC_COST_TOOLTIP_MSG, EXTENDED_SELL_TOOLTIP_MSG, LEAD_TIME_UNITS, priceRelatedCols, serviceRelatedCols } from '../constants';
import { MainProductDataType } from '../../mainProductTable';
import { OptionDataType } from '../../optionsTable';
import { PricingColChangeCallback } from './types';
import { LOT_ASSIGNED_KIT_MAIN_LINE_ITEM, KIT_LINE_TOOLTIP_MSG, LOT_ASSIGNED_TOOLTIP_MSG, DISCOUNT_DISCOUNT_COST_MARGIN_TOOLTIP_MSG } from '../constants';
import { getAdjacentColumnNames, getParsedContentFromClipboard } from 'src/features/BOM/shared/utils';
import { BOM_TABLES_ENUM, ROW_HEIGHT, ROW_LEVEL } from 'src/features/BOM/shared/constants';
import { CurrentDropNodeType } from 'src/features/BOM/queries/apiTypes';
import { getAddOrDeductFooterExtendedPriceSum } from '../../optionsTable/utils';
import { getExtendedSums } from '../../mainProductTable/utils';
import { isValidCharactersForMfg } from 'src/features/Settings/Manufacturer/utils';
import { INVALID_CHARACTERS_IN_MFG_NAME } from 'src/features/Settings/Manufacturer/constants';

export function getValueGetterForPricingCols(value: ValueGetterParams) {
    return value?.data?.[value.column.getColId() as BOM_TABLES_ENUM];
}

export function getPricingColumnDisabled(params: any) {
    const fieldName = params.column.getColId();
    if (priceRelatedCols.has(fieldName as BOM_TABLES_ENUM) && fieldName !== BOM_TABLES_ENUM.COST && fieldName !== BOM_TABLES_ENUM.SELL_PRICE) {
        return !params.data.cost;
    }
    return false;
}

export function getEditableHandlerForPricingColumns(params: any) {
    return !getPricingColumnDisabled(params);
}

export const DISABLED_COLOR_HEX = '#f1f5f9';

export function getColorHandlerForPricingColumns(params: any): CellStyle {
    if (getPricingColumnDisabled(params) && !params.node.rowPinned) {
        return { backgroundColor: DISABLED_COLOR_HEX };
    }
    if (params?.node?.rowPinned === 'bottom') {
        return { border: 'none' };
    }

    // deliberately added wrong value so that it will accept bg color set by ag grid
    return {};
}

export function getIsEditableForMainTable(params: any) {
    const fieldName = params?.column?.colId;
    const isKit = params?.data?.is_kit && priceRelatedCols.has(fieldName);
    const isPricingCellEmpty =
        priceRelatedCols.has(fieldName) && (params?.data?.lotId !== null || (!params?.data?.cost && fieldName !== BOM_TABLES_ENUM.COST && fieldName !== BOM_TABLES_ENUM.SELL_PRICE));
    if (isPricingCellEmpty || (fieldName === BOM_TABLES_ENUM.LOT && params?.data?.hasOptions) || params?.node?.rowPinned || isKit || (fieldName === BOM_TABLES_ENUM.LOT && params?.data?.kit)) {
        return false;
    }

    return true;
}

export function getColorHandlerForMainTable(args: any): CellStyle {
    if (!getIsEditableForMainTable(args) && !args?.node?.rowPinned) {
        return { backgroundColor: DISABLED_COLOR_HEX };
    }

    if (args?.node?.rowPinned === 'bottom') {
        return { border: 'none' };
    }

    // deliberately added wrong value so that it will accept bg color set by ag grid
    return {};
}

export function getFooterDisabled(args: any) {
    if (args?.node?.rowPinned === 'bottom') {
        return { border: 'none' };
    }
    return;
}

export const getTemplateCellStyle = (params: any): CellStyle => {
    if (params.node.rowPinned === 'bottom') {
        return { border: 'auto' };
    }
    if (!params.node.group && params?.node?.level === 0) {
        return {};
    }
    return { backgroundColor: 'unset' };
};

export const isLeadTimeValueEditable = (params: any) => {
    if (params?.node?.data?.leadTimeUnit === LEAD_TIME_UNITS.IN_STOCK.toLowerCase()) {
        return false;
    }

    return true;
};

export const getleadTimeValueDisableColor = (params: any): CellStyle => {
    if (!isLeadTimeValueEditable(params)) {
        return { backgroundColor: DISABLED_COLOR_HEX };
    }

    return {};
};

export function getValueSetterForPricingCols(value: ValueSetterParams, pricingColChangeCallback: PricingColChangeCallback) {
    const field = value.column.getColId() as BOM_TABLES_ENUM;
    const hasValueChanged = value.data[field] !== value.newValue;
    if (hasValueChanged) {
        const newData = pricingColChangeCallback({ ...value.data, [field]: value.newValue, ...value?.context }, field);
        Object.keys(newData).forEach((key: string) => {
            value.data[key] = newData[key as keyof typeof newData];
        });
    }
    return hasValueChanged;
}

export const pricingFieldToolTipValueGetter = (params: ITooltipParams<MainProductDataType>, field = ``) => {
    const { data, node } = params;
    const isComponentsTable = Boolean(data?.kit) && node?.level === ROW_LEVEL.COMPONENT;
    let doesKitHasLotAssigned = data?.kit && node?.parent?.data?.lotId != null ? true : false;
    if (params.node?.level === ROW_LEVEL.SECTION) return;
    if (data?.is_kit && data?.lotId) return LOT_ASSIGNED_KIT_MAIN_LINE_ITEM;
    if (data?.is_kit) return KIT_LINE_TOOLTIP_MSG;
    if (data?.lotId || (isComponentsTable && doesKitHasLotAssigned)) return LOT_ASSIGNED_TOOLTIP_MSG;
    if (!(field === BOM_TABLES_ENUM.COST || field === BOM_TABLES_ENUM.SELL_PRICE) && !getIsEditableForMainTable(params)) return DISCOUNT_DISCOUNT_COST_MARGIN_TOOLTIP_MSG;
    if (field === BOM_TABLES_ENUM.EXTENDED_PRICE && !node?.rowPinned) {
        return EXTENDED_SELL_TOOLTIP_MSG;
    }
    if (field === BOM_TABLES_ENUM.EXT_COST && !node?.rowPinned) {
        return EXTENDED_COST_TOOLTIP_MSG;
    }
    if (field === BOM_TABLES_ENUM.EXT_DISCOUNT_COST && !node?.rowPinned) {
        return EXTENDED_DISC_COST_TOOLTIP_MSG;
    }
    return '';
};

export const optionsToolTipValueGetter = (params: ITooltipParams<any, any, any>, field = ``) => {
    if (params.node?.level === ROW_LEVEL.SECTION) return;

    if (params?.data?.is_kit) return KIT_LINE_TOOLTIP_MSG;
    if (!(field === BOM_TABLES_ENUM.COST || field === BOM_TABLES_ENUM.SELL_PRICE) && !getIsEditableForMainTable(params)) return DISCOUNT_DISCOUNT_COST_MARGIN_TOOLTIP_MSG;
    if (field === BOM_TABLES_ENUM.EXT_DISCOUNT_COST && !params?.node?.rowPinned) {
        return EXTENDED_DISC_COST_TOOLTIP_MSG;
    }
    return '';
};

export const handleProcessCellPasteForPricing = (
    args: ProcessCellForExportParams,
    startColumnOnPasteRef: MutableRefObject<string | undefined>,
    setSnackbarOpen?: (message: string, autoHideDuration?: number | null | undefined) => void
) => {
    const { column, value } = args;
    const fieldName = column.getColId();

    if (!startColumnOnPasteRef?.current) {
        // While pasting contents from clippoard, I need to track the first column on which the cell edit callback will be fired.
        startColumnOnPasteRef.current = fieldName;
    }

    if (fieldName === 'manufacturer') {
        const { isValid, sanitizedValue } = isValidCharactersForMfg(value?.trim());
        if (!isValid) {
            if (setSnackbarOpen) setSnackbarOpen(INVALID_CHARACTERS_IN_MFG_NAME);
            return sanitizedValue;
        }
    }

    if (fieldName === 'qty') {
        if (value !== null && value !== '' && !isNaN(value as unknown as number)) {
            return Number(value);
        } else {
            return null;
        }
    } else if (value === '' || value == null) {
        return fieldName === BOM_TABLES_ENUM.LEAD_TIME_VALUE ? null : '';
    }

    const processCellPasteCols = new Set([...priceRelatedCols, ...serviceRelatedCols]);

    if (processCellPasteCols.has(fieldName)) {
        const newValue: string = `${value}`.replace(/\$|,|%/g, '');
        if (newValue !== '' && !isNaN(newValue as unknown as number)) {
            return parseFloat(newValue);
        }
        return null;
    }
    return value;
};

export const isDisabledCellClassRules = {
    'ag-grid-disable-cell': (props: CellClassParams) => !props.node.group && props?.node?.level === 0
};
export const isSectionEditingNotAllowed = (props: CellClassParams) => !(props?.node?.level === 0);

// This needs to be removed from here and has already a common function
export function setPotentialParentRowDragAndDropNode(
    api: GridApi,
    overNode: IRowNode | null | undefined,
    potentialParent: MutableRefObject<IRowNode | null>,
    currentDropNode?: MutableRefObject<CurrentDropNodeType | null>,
    mousePosition?: number
) {
    const newPotentialParent = overNode ? (overNode.level === 0 || (overNode.level === 1 && overNode.allChildrenCount) || overNode.data.is_kit ? overNode : overNode.parent) : null;
    const newCurrentDropNode = overNode ? overNode : null;

    if (potentialParent?.current === newPotentialParent && currentDropNode?.current === newCurrentDropNode) return;

    const rowsToRefresh = [];
    if (potentialParent.current) rowsToRefresh.push(potentialParent.current);
    if (newPotentialParent) rowsToRefresh.push(newPotentialParent);
    if (currentDropNode?.current) rowsToRefresh.push(currentDropNode.current);
    if (newCurrentDropNode) rowsToRefresh.push(newCurrentDropNode);

    potentialParent.current = newPotentialParent;

    if (currentDropNode) {
        currentDropNode.current = newCurrentDropNode ? { ...newCurrentDropNode, yPosition: mousePosition } : null;
    }
    refreshRows(api, rowsToRefresh);
}

// THIS AS WELL NEEDS TO BE REMOVED
function refreshRows(api: GridApi, rowsToRefresh: IRowNode[]) {
    if (rowsToRefresh.length) {
        api.refreshCells({ rowNodes: rowsToRefresh });
    }
}
export const updateDragIcon = (isAllowed: boolean) => {
    const dragIcon = document.querySelector('.ag-dnd-ghost .ag-dnd-ghost-icon .ag-icon');
    if (dragIcon) {
        dragIcon.classList.replace(isAllowed ? 'ag-icon-not-allowed' : 'ag-icon-arrows', isAllowed ? 'ag-icon-arrows' : 'ag-icon-not-allowed');
    }
};

const sumSectionPrice = (rowNode: IRowNode, field: 'extendedPrice' | 'addDeductAmount' | 'extendedDiscountCost') => {
    if (rowNode.level === ROW_LEVEL.SECTION) {
        return rowNode.allLeafChildren.reduce((acc, item) => {
            if (item.level === ROW_LEVEL.PRODUCT && item.data[field] && !item.data.is_hidden) {
                acc += item.data[field];
            }
            return acc;
        }, 0);
    }
    return;
};
const sumSectionAddDeductPrice = (rowNode: IRowNode, field: 'addDeductAmount') => {
    if (rowNode.level === ROW_LEVEL.SECTION) {
        const aggsum = getAddOrDeductFooterExtendedPriceSum(rowNode.allLeafChildren.map((item) => item.data));

        const sumNow = sumSectionPrice(rowNode, field);
        return aggsum === 'N/A' ? aggsum : sumNow;
    }
    return;
};
const sumSectionExtSellPrice = (rowNode: IRowNode, field: 'extendedPrice') => {
    if (rowNode.level === ROW_LEVEL.SECTION) {
        const aggsum = getExtendedSums(
            rowNode.allLeafChildren.map((item) => item.data),
            field
        );
        return aggsum;
    }
    return;
};
const sumSectionExtCost = (rowNode: IRowNode) => {
    if (rowNode.level === ROW_LEVEL.SECTION) {
        const aggsum = getExtendedSums(
            rowNode.allLeafChildren.map((item) => item.data),
            'extendedCost'
        );
        return aggsum;
    }
    return;
};
const sumSectionExtDiscountCost = (rowNode: IRowNode) => {
    if (rowNode.level === ROW_LEVEL.SECTION) {
        const aggsum = getExtendedSums(
            rowNode.allLeafChildren.map((item) => item.data),
            'extendedDiscountCost'
        );
        return aggsum;
    }
    return;
};
const percentageMargin = (rowNode: IRowNode) => {
    if (rowNode.level === ROW_LEVEL.SECTION) {
        const rows = rowNode.allLeafChildren.map((item) => item.data);
        const extDiscountedCost = Number(getExtendedSums(rows, 'extendedDiscountCost'));
        const extSellPrice = Number(getExtendedSums(rows, 'extendedPrice'));
        if (isNaN(extDiscountedCost) || isNaN(extSellPrice)) {
            return 'N/A';
        } else if (extSellPrice === 0) {
            return;
        } else {
            return (1 - extDiscountedCost / extSellPrice) * 100;
        }
    }
    return;
};
const percentageDiscount = (rowNode: IRowNode) => {
    if (rowNode.level === ROW_LEVEL.SECTION) {
        const rows = rowNode.allLeafChildren.map((item) => item.data);
        const extCostSum = Number(getExtendedSums(rows, 'extendedCost'));
        const extDiscountedCost = Number(getExtendedSums(rows, 'extendedDiscountCost'));
        if (isNaN(extDiscountedCost) || isNaN(extCostSum)) {
            return 'N/A';
        } else if (extCostSum === 0) {
            return;
        } else {
            return (1 - extDiscountedCost / extCostSum) * 100;
        }
    }
    return;
};
export const aggFunctions = {
    addQtySum: (params: IAggFuncParams) => {
        const { rowNode } = params;

        if (rowNode.level === ROW_LEVEL.SECTION) {
            const sectionQtyCount = rowNode.allLeafChildren.reduce((acc, item) => {
                if (item.level === ROW_LEVEL.PRODUCT && item.data.qty && !item.data.is_hidden) {
                    acc += item.data.qty;
                }
                return acc;
            }, null);
            return sectionQtyCount;
        }
        return;
    },

    sumExtCost: (params: IAggFuncParams) => sumSectionExtCost(params.rowNode),
    sumExtPrice: (params: IAggFuncParams) => sumSectionExtSellPrice(params.rowNode, 'extendedPrice'),
    percentageMargin: (params: IAggFuncParams) => percentageMargin(params.rowNode),
    percentageDiscount: (params: IAggFuncParams) => percentageDiscount(params.rowNode),
    sumExtDisCountPrice: (params: IAggFuncParams) => sumSectionExtDiscountCost(params.rowNode),
    sumAddDeductPrice: (params: IAggFuncParams) => sumSectionAddDeductPrice(params.rowNode, 'addDeductAmount')
};

export const isPricingFieldsEditable = (params: EditableCallbackParams<MainProductDataType | OptionDataType>) => {
    const { api, data, node } = params;
    const isKitTable = data?.is_kit;
    const isKit = data?.kit;
    const isComponentsTable = Boolean(isKit) && node?.level === ROW_LEVEL.COMPONENT;

    if (isKitTable) return false;
    if (isComponentsTable) {
        const kitDetails = api.getRowNode(isKit as unknown as string);
        return !kitDetails?.data?.lotId && getIsEditableForMainTable(params);
    }

    return getIsEditableForMainTable(params);
};

export const handleOnRangeSelectionChangedForLotAndServices = (event: RangeSelectionChangedEvent, tableRef: React.RefObject<AgGridReact>, selectedRowsRef: MutableRefObject<Set<number>>) => {
    const cellRanges = tableRef.current?.api.getCellRanges();
    if (!cellRanges || cellRanges.length === 0) {
        return;
    }
    selectedRowsRef.current.clear();
    let startRow = 0,
        endRow = 0;
    if (cellRanges) {
        cellRanges.forEach((range) => {
            startRow = Math.min(Number(range.startRow?.rowIndex), Number(range.endRow?.rowIndex));
            endRow = Math.max(Number(range.startRow?.rowIndex), Number(range.endRow?.rowIndex));
        });
    }
    if (event.finished) {
        const displayedRowCount = tableRef.current?.api?.getDisplayedRowCount(); // Get the total count of displayed rows
        let rowIndex = endRow === startRow ? endRow : startRow; // Determine the starting row index

        if (displayedRowCount) {
            for (; rowIndex < displayedRowCount; rowIndex++) {
                const rowNode = tableRef.current?.api?.getDisplayedRowAtIndex(rowIndex); // Get the row node at each index
                if (rowNode?.data?.id) {
                    selectedRowsRef.current.add(rowNode.data.id);
                }
            }
        }
    }
};

// THIS FUNCTION NEEDS TO BE REMOVED. ALREADY DEFINED IN A RELATIVELY TOP MODULE
export const isHighlightDropLine = (params: CellClassParams | RowDragEndEvent, currentDropNode: CurrentDropNodeType | null, isBottomLine: boolean) => {
    if (!currentDropNode || currentDropNode.yPosition === undefined) return false;

    const yPosition = currentDropNode.yPosition;
    const rowIndex = params.node.rowIndex;
    const currentRowIndex = currentDropNode.rowIndex;

    if (isBottomLine) {
        return rowIndex === currentRowIndex && yPosition > ROW_HEIGHT / 2;
    } else {
        return rowIndex === currentRowIndex && yPosition <= ROW_HEIGHT / 2;
    }
};

// NEED TO BE REMOVED. ALREADY DEFINED IN A RELATIVELY TOP MODULE
export const getSelectedSectionIds = (hierarchy: any): number[] => {
    const selectedIds: number[] = [];
    for (const [sectionId, sectionData] of Object.entries(hierarchy || {})) {
        if (sectionData && typeof sectionData === 'object' && 'isSelected' in sectionData && sectionData.isSelected === true) {
            selectedIds.push(Number(sectionId));
        }
    }
    return selectedIds;
};

export const checkCellHaveRangeSelection = (args: SelectionChangedEvent) => {
    const cellRanges = args.api?.getCellRanges();

    return (cellRanges || []).some((range) => {
        const startRowIndex = range.startRow?.rowIndex ?? 0;
        const endRowIndex = range.endRow?.rowIndex ?? 0;
        const hasMultipleRows = endRowIndex > startRowIndex;
        const hasMultipleColumns = range.columns.length > 1;
        return hasMultipleRows || hasMultipleColumns;
    });
};
interface ClearContentsParams {
    api: GridApi;
    context: GridApi['context'];
    selectedRanges: CellRange[];
    getSelectedNodes?: () => IRowNode[];
}
export const handleRightClickPasteOperation = async (params: ClearContentsParams) => {
    const { api, context, selectedRanges } = params;
    if (!selectedRanges?.length) return;

    try {
        const clipboardData = await getParsedContentFromClipboard();
        const startRow = selectedRanges[0]?.startRow?.rowIndex ?? 0;
        const startColumn = selectedRanges[0]?.columns?.[0];
        const startColumnName = startColumn?.getColId();

        const adjacentColumns = getAdjacentColumnNames(context.tableRef, startColumnName, ['id', 'menu', 'drag']);
        clipboardData.forEach((rowData, rowIndex) => {
            const node = api?.getDisplayedRowAtIndex(startRow + rowIndex);
            if (!node) return;

            rowData.forEach((cellValue, colIndex) => {
                const targetColumn = adjacentColumns?.[colIndex];
                if (!targetColumn?.field) return;

                const column = api.getColumnDef(targetColumn.field);
                const isColumnEditable =
                    typeof column?.editable === 'function'
                        ? column?.editable({
                              node,
                              data: node.data,
                              colDef: column,
                              column: api.getColumnDef(targetColumn.field) as any,
                              api,
                              columnApi: {} as any,
                              context
                          })
                        : column?.editable;

                if (isColumnEditable && cellValue !== undefined) {
                    const isNumericColumn = column?.cellDataType === 'number' || column?.cellEditor === 'agNumberCellEditor';
                    const processedValue = isNumericColumn ? (cellValue === '' || isNaN(Number(cellValue)) ? null : Number(cellValue)) : cellValue;
                    const targetedFieldName = targetColumn.field;
                    node.setDataValue(targetedFieldName, processedValue);
                }
            });
        });
    } catch (error) {
        console.error('Failed to process clipboard data:', error);
    }
};

export const handleRightClickClearSelection = ({ api, context, selectedRanges, getSelectedNodes }: ClearContentsParams): void => {
    if (!selectedRanges?.length || !getSelectedNodes) return;

    const columns = selectedRanges[0]?.columns ?? [];
    const rangeStartRow = selectedRanges[0]?.startRow?.rowIndex ?? 0;
    const rangeEndRow = selectedRanges[0]?.endRow?.rowIndex ?? rangeStartRow;

    const startRow = Math.min(rangeStartRow, rangeEndRow);
    const endRow = Math.max(rangeStartRow, rangeEndRow);

    for (let rowIndex = startRow; rowIndex <= endRow; rowIndex++) {
        const node = api.getDisplayedRowAtIndex(rowIndex);
        if (!node) continue;

        columns.forEach((column: Column) => {
            const colDef = column.getColDef();
            const isColumnEditable = typeof colDef.editable === 'function' ? colDef.editable({ node, data: node.data, colDef, column, api, columnApi: {} as any, context }) : colDef.editable;

            if (isColumnEditable) {
                node.setDataValue(column.getColId(), null);
            }
        });
    }
};

export const clearCheckBoxSelectionFromAPI = (params: CellContextMenuEvent) => {
    const selectedRows = params.api.getSelectedRows();
    if (selectedRows.length > 0) {
        params.api.forEachNode((node: IRowNode) => {
            node.setSelected(false);
        });
    }
};

export const handleRightClickContextMenuChange = (props: CellContextMenuEvent) => {
    const { event, node, column, value, api } = props;
    if (!event) return;

    const gridApi = api as any;
    const mouseEvent = event as MouseEvent;

    if (gridApi.contextMenuFactory.activeMenu) {
        gridApi.contextMenuFactory.hideActiveMenu();

        const syntheticEvent = {
            clientX: mouseEvent.clientX,
            clientY: mouseEvent.clientY,
            pageX: mouseEvent.pageX,
            pageY: mouseEvent.pageY,
            screenX: mouseEvent.screenX,
            screenY: mouseEvent.screenY,
            target: mouseEvent.target instanceof Element ? mouseEvent.target : (mouseEvent.target as any)?.element,
            type: 'contextmenu'
        };

        gridApi.contextMenuFactory.showMenu(node, column, value, syntheticEvent);
    }
};
